import React from 'react';

/**
 * Sentinel error thrown (and re-thrown) when lazyWithRetry triggers a hard
 * page reload to recover from a stale-chunk 404. Callers (ErrorBoundary)
 * can detect this type and render a "reloading…" state instead of an error
 * message, since the browser is already navigating away.
 */
export class ChunkReloadError extends Error {
  constructor() {
    super('Chunk load failed – reloading page to fetch latest assets.');
    this.name = 'ChunkReloadError';
  }
}

/**
 * Drop-in replacement for React.lazy that survives the "stale tab +
 * new deploy" chunk-load race.
 *
 * What goes wrong without this:
 *   1. User opens pacioli.altailabs.io on Monday. Vite serves
 *      `index.html` referencing chunks `SignUp-<hash_A>.js`.
 *   2. Tuesday morning we deploy. New build produces
 *      `SignUp-<hash_B>.js`; hash_A's file is gone from the CDN.
 *   3. User still has Monday's tab open. Clicks "Sign up" →
 *      `React.lazy(() => import('./pages/SignUp'))` fires with the
 *      old URL → 404 (or HTML from the SPA fallback) → rejected
 *      promise → React.lazy's internal `_result.default` is
 *      undefined → ErrorBoundary catches a generic TypeError deep
 *      inside react-dom's reconciler.
 *
 * What this does:
 *   - First failure: wait 500 ms and retry. Covers transient blips
 *     (flaky wifi, CDN hiccup) without reloading the tab.
 *   - Second failure: if we haven't already reloaded once this
 *     session, do a hard reload and throw a ChunkReloadError so
 *     React.lazy enters the "rejected" state (_status = 2) with a
 *     real Error object. This prevents the TypeError that arises when
 *     React tries to read `_result.default` on an undefined result,
 *     which happened when we previously returned a never-resolving
 *     promise after calling window.location.reload().
 *   - Third failure (after reload): let the original error propagate.
 *     Prevents infinite reload loops when the deploy itself is
 *     genuinely broken.
 *
 * The `vite:preloadError` listener in `main.tsx` already handles the
 * `<link rel="modulepreload">` path. This function handles the
 * complementary case where the dynamic import fires on user click
 * without a prior preload.
 */
export function lazyWithRetry<T extends React.ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return React.lazy(async () => {
    const validate = (mod: unknown): { default: T } => {
      // Vite's SPA fallback can return an HTML document for a stale
      // chunk URL — the dynamic import resolves to a module-shaped
      // object whose `default` is undefined. React's lazy state
      // machine then crashes with `undefined is not an object
      // (evaluating 'n._result.default')` (Sentry JAVASCRIPT-REACT-4
      // / JAVASCRIPT-REACT-5). Reject explicitly so the retry path
      // (and ultimately the page reload) takes over.
      if (
        mod &&
        typeof mod === 'object' &&
        'default' in mod &&
        (mod as { default: unknown }).default !== undefined
      ) {
        return mod as { default: T };
      }
      throw new Error('lazyWithRetry: resolved module has no default export');
    };
    try {
      return validate(await factory());
    } catch (err) {
      // One in-place retry before giving up.
      try {
        await new Promise((resolve) => setTimeout(resolve, 500));
        return validate(await factory());
      } catch (err2) {
        const SESSION_KEY = '__altai_chunk_reload_attempted';
        const alreadyReloaded =
          typeof sessionStorage !== 'undefined' && sessionStorage.getItem(SESSION_KEY) === '1';
        if (!alreadyReloaded && typeof window !== 'undefined') {
          try {
            sessionStorage.setItem(SESSION_KEY, '1');
          } catch {
            /* storage disabled — still try the reload */
          }
          window.location.reload();
          // Throw a typed sentinel so React.lazy's internal state machine
          // lands in the "rejected" state (_status = 2, _result = error).
          // Previously we returned `new Promise(() => {})` here, which
          // should keep the lazy pending, but in practice React could end
          // up with _status = 1 and _result = undefined — causing the
          // cryptic "Cannot read properties of undefined (reading 'default')"
          // TypeError. By throwing a real Error we guarantee _result is a
          // proper Error object; React re-throws it and the ErrorBoundary
          // can detect ChunkReloadError and render a "reloading…" UI.
          throw new ChunkReloadError();
        }
        // Already reloaded once this session — deploy is genuinely
        // broken. Let the ErrorBoundary render its fallback.
        throw err2;
      }
    }
  });
}
